import {observable, action} from 'mobx';
import get from 'lodash/get';
import {getRootStore} from '../../app.bootstrap';
import {getDashboardStores} from '../../states/dashboard/dashboard.stores';

export interface IHistorySecurity {
    enabled: boolean;
}

export interface IHistoryResource {
    _id: string;
    url: string;
    roomId?: string;
    storageType: 'TEMP' | 'S3';
    isVideo?: boolean;
    security?: IHistorySecurity;
}

export interface IHistoryRecord {
    timestamp: string;
    roomId: string;
    images: IHistoryResource[];
    videos: IHistoryResource[];
    messages: string[];
    files: string[];
}

export interface ICustomerHistory {
    records: IHistoryRecord[];
    images: number;
    videos: number;
    messages: number;
    files: number;
}

export class CustomerHistoryModalController {
    @observable isVisible = false;
    @observable history: ICustomerHistory | null = null;
    @observable dir: 'ltr' | 'rtl' = 'ltr';

    private _resolve?: (val: {resource: IHistoryResource; room: string} | undefined) => void;

    constructor() {}

    // Get the service from DashboardStores when needed
    private get historyService() {
        return getDashboardStores().historyService;
    }

    @action
    open = (
        history: ICustomerHistory,
        dir: 'ltr' | 'rtl'
    ): Promise<{resource: IHistoryResource; room: string} | undefined> => {
        this.history = history;
        this.dir = dir;
        this.isVisible = true;

        return new Promise((resolve) => {
            this._resolve = resolve;
        });
    };

    @action
    close = (result?: {resource: IHistoryResource; room: string}) => {
        this.isVisible = false;
        this._resolve?.(result);
    };

    private _updateSecureResource = async (resource: IHistoryResource): Promise<IHistoryResource> => {
        if (get(resource, 'security.enabled')) {
            const resourceType = resource.isVideo ? 'VIDEO' : 'IMAGE';

            try {
                await this.historyService.resignResource(resource._id, resourceType);
                // The resignResource method returns void, so we return the original resource
                // The resource URL should be updated on the server side
                return resource;
            } catch (error) {
                console.error('Error resigning resource:', error);
                throw error;
            }
        }

        return resource;
    };

    annotateResource = async (resource: IHistoryResource, roomId: string) => {
        const updated = await this._updateSecureResource(resource);
        this.close({resource: updated, room: roomId});
    };

    translate = (key: string): string => {
        return getRootStore().localizationService.translate(key);
    };

    getLocale = (): string => {
        return getRootStore().tsLocaleHelper.getLocale();
    };
}
